import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import {API, BASE_API} from '../config/apis';
import i18n from 'i18next';

// Same request as API.GET_PUMP_API plus a required `fuelType`.
const GET_PUMP_BY_FUEL_API = BASE_API + 'maps/gas-stations-by-fuel';

// maps/gas-stations-by-fuel puts the price of the chosen fuel in
// `selectedFuel` (null when that fuel is not confirmed at the station) and
// leaves the old `latestPrice` / `createdAt` for some other fuel or empty.
// The map, cards and details sheet all read `latestPrice` / `createdAt`, so
// they are filled from `selectedFuel` here, once, and nothing else changes.
// A station without a confirmed price stays in the list with latestPrice null.
const toMilliseconds = isoDate => {
  const ms = isoDate ? Date.parse(isoDate) : NaN;
  return Number.isFinite(ms) ? ms : null;
};

const withSelectedFuelPrice = station => {
  if (!station) {
    return station;
  }
  const fuel = station.selectedFuel;
  return {
    ...station,
    latestPrice: fuel?.price ?? null,
    // The screens turn this into "Updated 3 hours ago" and expect a number
    // of milliseconds (the old API sent that). The new API sends an ISO date
    // text, which would be read as the year, so convert it here.
    createdAt: toMilliseconds(fuel?.updatedAt),
    // The receipt belongs to the station's latest scan, which may be another
    // fuel. Keep it only when that scan's price is this fuel's scanned price,
    // so the "Verified" badge and View Receipt show the receipt behind it.
    recieptUrl:
      fuel?.source === 'pompi' &&
      fuel.price != null &&
      Number(station.latestPrice) === Number(fuel.price)
        ? station.recieptUrl
        : null,
  };
};

const withSelectedFuelPrices = payload => {
  if (!payload) {
    return payload;
  }
  return {
    ...payload,
    cheapestStation: withSelectedFuelPrice(payload.cheapestStation),
    data: (payload.data || []).map(route => ({
      ...route,
      gasStations: (route.gasStations || []).map(withSelectedFuelPrice),
    })),
  };
};

// Pass `route` (an item from mapFlow/directions fetchRouteOptions) to get only
// the stations within 2 km of that route, in driving order. Without it the
// backend works out its own routes (old behaviour, used by searchFlow).
// Pass `fuelType` (for example 'diesel') to use maps/gas-stations-by-fuel:
// stations come back with the price of that fuel. Without it the old
// maps/gas-stations endpoint is used, exactly as before.
export const getGasStations = createAsyncThunk(
  'getGasStations',
  async (
    {sourceLat, sourceLng, destLat, destLng, route, fuelType},
    {rejectWithValue, getState},
  ) => {
    try {
      //fetch user details from redux store
      const token = getState().login.token;
      const userId = getState().login.userId;
      const response = await axios.post(
        fuelType ? GET_PUMP_BY_FUEL_API : API.GET_PUMP_API,
        {
          userId,
          sourceLat,
          sourceLng,
          destLat,
          destLng,
          ...(fuelType && {fuelType}),
          ...(route && {
            polyline: route.polyline,
            summary: route.summary,
            distanceMeters: route.distanceMeters,
            durationSeconds: route.durationSec,
          }),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Accept-Language': i18n.language === 'dt' ? 'du' : 'en',
          },
        },
      );
      return fuelType
        ? withSelectedFuelPrices(response.data)
        : response.data;
    } catch (err) {
      console.log('error', err);
      return rejectWithValue(
        err.response?.data?.message || err.response?.data?.Message || err.message,
      );
    }
  },
);

const gasStationSlice = createSlice({
  name: 'gasStation',
  initialState: {
    gasStations: [],
    cheapestGasStation: null,
    // Stations of the first route that the server left out (only sent by
    // gas-stations-by-fuel).
    hiddenCount: 0,
    loading: false,
    error: null,
    currentRequestId: null,
  },
  reducers: {
    // Shows a response kept from an earlier getGasStations call (or clears
    // the list with null) and drops any request still in flight.
    showGasStations: (state, action) => {
      state.gasStations = action.payload?.data?.[0]?.gasStations || [];
      state.cheapestGasStation = action.payload?.cheapestStation || null;
      state.hiddenCount = action.payload?.data?.[0]?.hiddenCount || 0;
      state.loading = false;
      state.error = null;
      state.currentRequestId = null;
    },
  },
  extraReducers: builder => {
    builder.addCase(getGasStations.pending, (state, action) => {
      state.loading = true;
      state.currentRequestId = action.meta.requestId;
    });
    builder.addCase(getGasStations.fulfilled, (state, action) => {
      // A newer request (e.g. for another route) replaced this one.
      if (action.meta.requestId !== state.currentRequestId) {
        return;
      }
      // Store the gas stations from the first route
      state.gasStations = action.payload?.data[0]?.gasStations || [];
      state.cheapestGasStation = action.payload?.cheapestStation || null;
      state.hiddenCount = action.payload?.data[0]?.hiddenCount || 0;
      state.loading = false;
      state.error = null;
    });
    builder.addCase(getGasStations.rejected, (state, action) => {
      if (action.meta.requestId !== state.currentRequestId) {
        return;
      }
      state.error = action.payload;
      state.loading = false;
    });
  },
});

export const {showGasStations} = gasStationSlice.actions;
export default gasStationSlice.reducer;